<template>
  <div v-if="salesperson" class="grid h-full grid-cols-1 grid-rows-[auto_1fr]">
    <!-- Header -->
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedSalesperson?.name ? modifiedSalesperson.name : '-'"
      link-name="salespersons-management"
      :mode="mode"
      :is-modified="isModified"
      :is-edit-active="salesperson.isFromManager || false"
      @edit-mode="onEditMode"
      @cancel="onCancelEdit"
      @save="onSaveChanges"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <AppResizableContainer
      :layout="isHorizontalLayout ? 'vertical' : 'horizontal'"
      :minSize="300"
      class="overflow-auto">
      <template #headerTitle v-if="isHorizontalLayout">
        <nav
          role="section-nav"
          class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
          <ul class="flex flex-wrap gap-4 items-center pl-4 -mb-px">
            <li
              v-for="tab in section1Tabs"
              :key="tab.name"
              @click="selectedTab = tab.name"
              class="py-1.5 cursor-pointer"
              :class="[
                selectedTab === tab.name ? 'border-b-2 border-theme-500' : '',
                tab.validator &&
                tab.validator.some((item) => {
                  return $v[item].required.$invalid;
                })
                  ? 'text-red-500 !border-red-500'
                  : '',
              ]">
              <p>
                {{ tab.label }}
              </p>
            </li>
          </ul>
        </nav>
      </template>
      <template #headerContent>
        <!-- Horizontal Layout -->
        <template v-if="isHorizontalLayout">
          <component
            :is="currentComponent"
            v-model="modifiedSalesperson"
            :salesperson="salesperson"
            :mode="mode"
            :isHorizontalLayout="isHorizontalLayout"
            :key="salesperson?.id"
            :validations="$v" />
        </template>

        <!-- Vertical Layout -->
        <template v-else>
          <PerfectScrollbar class="py-4 space-y-2 w-full h-full" ref="scrollbarId">
            <BaseAccordion
              v-for="tab in section1Tabs"
              :key="tab.name"
              :name="tab.label"
              :error="
                tab.validator &&
                tab.validator.some((item) => {
                  return $v[item].required.$invalid;
                })
              ">
              <SalespersonDetailsTabProperties
                v-if="tab.name === 'properties'"
                v-model="modifiedSalesperson"
                :salesperson="salesperson"
                :mode="mode"
                :isHorizontalLayout="isHorizontalLayout"
                :validations="$v" />

              <SalespersonDetailsTabInfo
                v-else-if="tab.name === 'info'"
                v-model="modifiedSalesperson"
                :salesperson="salesperson"
                :mode="mode"
                :isHorizontalLayout="isHorizontalLayout" />
            </BaseAccordion>
          </PerfectScrollbar>
        </template>
      </template>
    </AppResizableContainer>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { helpers, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { deepCopy } from '@/utils/data';
import { useToastStore } from '@/stores/toast';

import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import BaseAccordion from '@/components/common/BaseAccordion.vue';
import SalespersonDetailsTabProperties from '@/components/salespersonsManagement/SalespersonDetailsTabProperties.vue';
import SalespersonDetailsTabInfo from '@/components/salespersonsManagement/SalespersonDetailsTabInfo.vue';

// Types
import type { SalesPerson } from '@/types/SalesPersons';
import type { PerfectScrollbarExpose } from 'vue3-perfect-scrollbar';

// Services
import { addOrEditSalesperson, getSalesPersonById } from '@/services/api/user';

const route = useRoute();
const router = useRouter();
const { t: $t } = useI18n();

const isHorizontalLayout = ref(true);
const mode = ref<'view' | 'edit'>('view');
const selectedTab = ref('properties');

const salesperson = ref<SalesPerson>();
const modifiedSalesperson = ref<SalesPerson>();
const resetTablesState = ref(true);

async function loadData() {
  const id = route.params.id as string;
  const res = await getSalesPersonById(id);
  if (res) {
    salesperson.value = res as SalesPerson;
    mode.value = 'view';
  } else {
    salesperson.value = initializeSalesperson(id);
    salesperson.value.id = id;
    mode.value = 'edit';
  }
  modifiedSalesperson.value = deepCopy<SalesPerson>(salesperson.value);
}

function initializeSalesperson(id: string): SalesPerson {
  return {
    id,
    name: '',
    code: '',
    userID: '',
    onlineSearchLevel: 1,
    rootCode: '0',
    sequence: 0,
    mwSalesPersonTypeID: undefined,
    salesPersonParentID: undefined,
    companySiteID: undefined,
    isActive: true,
    isFromManager: true,
  };
}

onMounted(async () => {
  await loadData();
});

// Tab configuration
const section1Tabs = ref([
  {
    name: 'properties',
    label: $t('customer.properties'),
    component: markRaw(SalespersonDetailsTabProperties),
    validator: ['name', 'code'],
  },
  {
    name: 'info',
    label: $t('salesperson.info'),
    component: markRaw(SalespersonDetailsTabInfo),
    validator: [],
  },
]);

// Validation setup
const validationModel = ref({
  name: modifiedSalesperson.value?.name || '',
  code: modifiedSalesperson.value?.code || '',
  userId: modifiedSalesperson.value?.userID || '',
  companySiteId: modifiedSalesperson.value?.companySiteID || '',
  mwSalesPersonTypeId: modifiedSalesperson.value?.mwSalesPersonTypeID || '',
  mwMaxLevelId: modifiedSalesperson.value?.mwSalesPersonsAccessMaxLevelID || '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.required'), required) },
  code: { required: helpers.withMessage($t('messages.errors.required'), required) },
  userId: { required: helpers.withMessage($t('messages.errors.required'), required) },
  companySiteId: { required: helpers.withMessage($t('messages.errors.required'), required) },
  mwSalesPersonTypeId: { required: helpers.withMessage($t('messages.errors.required'), required) },
  mwMaxLevelId: { required: helpers.withMessage($t('messages.errors.required'), required) },
}));

const $v = useVuelidate(rules, validationModel);

const currentComponent = computed(() => {
  const selected = section1Tabs.value.find((tab) => tab.name === selectedTab.value);
  return selected ? selected.component : null;
});

const isModified = ref<boolean>(false);

watch(
  () => modifiedSalesperson.value,
  (newData) => {
    isModified.value = JSON.stringify(newData) !== JSON.stringify(salesperson.value);
    validationModel.value.name = newData?.name || '';
    validationModel.value.code = newData?.code || '';
    validationModel.value.userId = newData?.userID || '';
    validationModel.value.companySiteId = newData?.companySiteID || '';
    validationModel.value.mwSalesPersonTypeId = newData?.mwSalesPersonTypeID || '';
    validationModel.value.mwMaxLevelId = newData?.mwSalesPersonsAccessMaxLevelID || '';
  },
  { deep: true }
);

const confirmModalActive = ref(false);
const isBackMode = ref(false);

function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else if (!salesperson.value?.createdDate) {
    router.push('/salespersons-management');
  }
  mode.value = 'view';
}

async function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  modifiedSalesperson.value = salesperson.value
    ? deepCopy<SalesPerson>(salesperson.value)
    : undefined;
  resetTablesState.value = true;
  if (!salesperson.value?.createdDate || isBackMode.value) {
    router.push('/salespersons-management');
  }
}

const toastStore = useToastStore();
async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) return;

  mode.value = 'view';
  if (
    isModified.value &&
    modifiedSalesperson.value &&
    (await addOrEditSalesperson(modifiedSalesperson.value))
  ) {
    toastStore.error({ text: `${$t('common.toast.error')}` });
  } else {
    await loadData();
    resetTablesState.value = true;
    toastStore.success({ text: `${$t('common.toast.success')}` });
  }
}

const scrollbarId = ref<PerfectScrollbarExpose>();

function onEditMode() {
  mode.value = 'edit';
  resetTablesState.value = false;
}
</script>
